<template>
  <div class="content">
    <h1 class="title">{{ $t("orders.title") }}</h1>
    <div class="table">
      <div class="inputs">
        <v-row>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <div class="flatpickr">
              <label
                for="date"
                :class="
                  labelForFlatpickr ? 'flatpickrLabel' : 'flatpickrHiddenLabel'
                "
                >{{ $t("orders.datePickerPlaceholder") }}</label
              >
              <div class="date-picker">
                <flat-pickr
                  v-model="date"
                  :config="config"
                  name="date"
                ></flat-pickr>
              </div>
            </div>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6" v-if="isSuperAdmin">
            <v-select
              :label="$t('capacity.partnerLabel')"
              v-model="partner"
              :items="partnersWithId"
              item-title="name"
              item-value="id"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-text-field
              :label="$t('orders.namePlaceholder')"
              v-model="name"
              @keydown.enter="handleFilter(false)"
            ></v-text-field>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-select
              :label="$t('anomaly.providerPlaceholder')"
              v-model="payment_provider"
              :items="providerTypes"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
        </v-row>
        <v-btn @click="handleFilter(false)" class="mb-2">{{
          $t("orders.filter")
        }}</v-btn>
      </div>
      <v-data-table
        v-if="!isLoading"
        :headers="headers"
        :items="orders"
        hide-default-footer
        :items-per-page="-1"
        class="elevation-0"
      >
        <template v-slot:[`item.transaction.payment_date`]="{ item }">
          <td
            v-if="
              item.selectable.transaction &&
              item.selectable.transaction.payment_date
            "
          >
            {{ dateTimeFormat(item.selectable.transaction.payment_date) }}
          </td>
        </template>
        <template v-slot:[`item.invoice.created_at`]="{ item }">
          <td
            v-if="item.selectable.invoice && item.selectable.invoice.created_at"
          >
            {{ dateTimeFormat(item.selectable.invoice.created_at) }}
          </td>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <td v-if="item.selectable.status">
            {{ $t(`orders.table.status.${item.selectable.status}`) }}
          </td>
        </template>
        <template v-slot:[`item.payment_provider`]="{ item }">
          <td v-if="item.selectable.payment_provider">
            {{ item.selectable.payment_provider }}
          </td>
        </template>
        <template v-slot:[`item.invoice`]="{ item }">
          <a
            v-if="
              item.selectable.invoice && item.selectable.invoice.response_data
            "
            :href="
              item.selectable.invoice && item.selectable.invoice.invoice_link
            "
            target="_blank"
          >
            {{ getInvoiceNumber(item.selectable.invoice.response_data) }}
          </a>
        </template>
        <template v-slot:[`item.used`]="{ item }">
          <td v-if="item.selectable.used">
            {{ $t(`orders.table.result.${item.selectable.used}`) }}
          </td>
        </template>
        <template v-slot:[`item.icon`]="{ item }">
          <v-btn
            @click="handleViewButton(item.selectable.id)"
            icon="mdi-eye"
            variant="text"
          ></v-btn>
        </template>
        <template v-slot:no-data>
          <Vue3Lottie
            :animationData="NoDataLottie"
            :height="200"
            :width="200"
          />
          <div class="pb-5 table-no-data">
            <h3>{{ $t("tableNoDataTitle") }}</h3>
            <p>{{ $t("tableNoDataDesc") }}</p>
          </div>
        </template>
        <template v-slot:bottom>
          <div class="botSide">
            <v-row
              align="center"
              justify-sm="end"
              justify="center"
              class="px-2"
            >
              <v-col cols="12" xs="12" lg="3" md="4" sm="6">
                <v-row align="center" justify="end"
                  ><div class="pr-2 pageSelectorLabel">
                    {{ $t("orders.tableFooterText") }}
                  </div>
                  <v-select
                    v-model="limit"
                    hide-details
                    @update:model-value="handleFilter(false)"
                    :items="pageSizes"
                    class="pageSelector pl-5"
                  ></v-select>
                  <div class="pl-2">
                    {{ displayRange }}
                  </div>
                </v-row>
              </v-col>
              <v-col
                cols="12"
                xs="12"
                lg="3"
                md="4"
                sm="5"
                class="pl-1 pr-1 pagination"
              >
                <v-pagination
                  v-model="page"
                  :length="pageCount"
                  rounded="circle"
                  :total-visible="1"
                  show-first-last-page="true"
                  @update:model-value="handleFilter(true)"
                ></v-pagination>
              </v-col>
            </v-row>
          </div>
        </template>
      </v-data-table>
      <v-card v-else class="loading">
        <v-progress-circular
          v-if="isLoading"
          size="73.5"
          indeterminate
          class="loading-circle"
        ></v-progress-circular>
      </v-card>
    </div>
  </div>
</template>

<script>
import { Vue3Lottie } from "vue3-lottie";
import NoDataLottie from "@/assets/lotties/nodatalottie.json";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import OrderDataPopper from "@/components/global/popperComponents/orderDataPopper/OrderDataPopper";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { Hungarian } from "flatpickr/dist/l10n/hu.js";
import { xml2json } from "xml-js";
import { PAYMENT_PROVIDER } from "../../../components/enums";

export default {
  name: "Orders",
  props: {},
  components: {
    flatPickr,
    Vue3Lottie,
  },
  data() {
    return {
      NoDataLottie,
      headers: [
        {
          align: "start",
          key: "transaction.payment_date",
          sortable: false,
          title: this.$t("orders.table.headers.transactionCreatedAt"),
        },
        {
          title: this.$t("orders.table.headers.transactionId"),
          key: "transaction.transaction_id",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.orderId"),
          key: "order_identifier",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.status"),
          key: "status",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.provider"),
          key: "payment_provider",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.fullName"),
          key: "full_name",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.invoiceCreated"),
          key: "invoice.created_at",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.invoiceNumber"),
          key: "invoice",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.endPriceGross"),
          key: "end_price_gross",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.usage"),
          key: "used",
          sortable: false,
        },
        {
          title: this.$t("orders.table.headers.icon"),
          key: "icon",
          align: "center",
          sortable: false,
        },
      ],

      hiddenLabel: false,

      //Pagination settings
      page: 1,
      limit: 5,
      total: 0,
      pageSizes: [10, 25, 50, 100],

      //For Search
      partner: undefined,
      partnersWithId: [],
      name: "",
      date: "",
      payment_provider: undefined,
      providerTypes: [
        this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`),
        this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`),
      ],

      orders: [],

      // For Flatpicker
      config: {
        altFormat: "Y-m-d",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
        locale: {
          ...Hungarian,
          rangeSeparator: " - ",
        },
        allowInput: false,
        enableTime: false,
        disableMobile: true,
        onOpen: () => {
          this.hiddenLabel = true;
        },
        onClose: () => {
          this.hiddenLabel = false;
        },
        onKeyDown: (selectedDates, dateStr, instance, event) => {
          if (event.key === "Enter") {
            instance.close();
            this.handleFilter(false);
          }
        },
      },

      isLoading: true,

      enterClicked: false,
    };
  },
  created() {
    this.getPartnerList();
    this.getOrderList();
  },

  methods: {
    ...mapActions({
      getPartners: "users/getPartners",
      getOrders: "users/getOrders",
      getUserOrders: "users/getUserOrders",
    }),

    handleViewButton(currentId) {
      this.emitter.emit("showPopper", {
        bind: {
          is: OrderDataPopper,
          headerTitle: "Megrendelés adatlapja",
          id: currentId,
        },
      });
    },

    getOrderList(body) {
      this.isLoading = true;
      this.getOrders(body)
        .then((res) => {
          this.isLoading = false;
          this.orders = res.list;
          this.page = res.page;
          this.limit = res.limit;
          this.total = res.total;
        })
        .catch((err) => {
          console.log(err);
          this.isLoading = false;
          this.error = true;
        });
    },

    handleFilter(hasPage, keydown) {
      if (keydown) {
        this.enterClicked = true;
      }
      const date_from = this.date !== null ? this.date.split(" -") : "";
      const date_to = this.date !== null ? this.date.split("- ") : "";
      const requestBody = {
        date_from: date_from[0] !== undefined ? String(date_from[0]) : "",
        date_to: date_to[1] !== undefined ? String(date_to[1]) : "",
        par_id: this.partner,
        full_name: this.name,
        payment_provider: this.payment_provider
          ? this.providerForSending(this.payment_provider)
          : null,
        limit: this.limit,
        page: hasPage ? this.page : 1,
      };
      console.log(requestBody);
      this.getOrderList(requestBody);
      setTimeout(() => {
        this.enterClicked = false;
      }, 100);
    },

    getPartnerList() {
      if (this.isSuperAdmin) {
        this.getPartners()
          .then((response) => {
            this.partnersWithId = response.result.map((partner) => {
              return { id: partner.id, name: partner.par_name };
            });
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },

    getInvoiceNumber(responseData) {
      if (responseData.includes("cmfx")) {
        const parts = responseData.split("|");
        if (parts.length >= 3) {
          return parts[2];
        }
      } else if (responseData.includes("szamlazz")) {
        const json = JSON.parse(xml2json(responseData));
        return json.elements[0].elements.find(
          (element) => element.name === "szamlaszam"
        ).elements[0].text;
      }
      return "";
    },

    dateTimeFormat(dateTime) {
      return moment(dateTime).format("YYYY.MM.DD HH:mm:ss");
    },

    providerForSending(value) {
      const payment_provider = {
        [this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`)]:
          PAYMENT_PROVIDER.SIMPLE,
        [this.$t(
          `orders.table.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`
        )]: PAYMENT_PROVIDER.SZEP_CARD,
      };

      return payment_provider[value] || "";
    },
  },

  computed: {
    ...mapGetters({ loggedInUser: "users/loggedInUser" }),

    pageCount() {
      return Math.ceil(this.total / this.limit);
    },

    displayRange() {
      const start = (this.page - 1) * this.limit + 1;
      const end = Math.min(start + this.limit - 1, this.total);
      return `${start}-${end} of ${this.total}`;
    },

    labelForFlatpickr() {
      return !this.hiddenLabel && (this.date === "" || this.date === null);
    },

    isSuperAdmin() {
      return this.loggedInUser && this.loggedInUser.role === 0;
    },
  },
};
</script>

<style lang="scss" scoped></style>
